import AlcoholCalcNavbar from "../Navbars/AlcoholCalcNavbar";
import React, {useState} from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {getCookie} from "../../tools/tools";
import Plot from 'react-plotly.js';


const AlcoholCalcV7 = () => {
    const [formData, setFormData] = useState({
        estimated_parameter: 'Biological fluid ethanol concentration',
        V: 500,
        M: 100,
        z: 0.4,
        t: 2,
        C: 1.21,
        r: 0.7,
        b: 0.15,
        a: 2,
        d: 0.3,
        k: 0.7,
        R: 1.3,
        eV: 20,
        eM: 1,
        ez: 0.01,
        et: 0.25,
        eC: 0.01,
        er: 0.1,
        eb: 0.025,
        ea: 1,
        ed: 0.05,
        ek: 0.3,
        eR: 0.05,
    });
    const [plotData, setPlotData] = useState(null);
    const names = {
        estimated_parameter: 'Biological fluid ethanol concentration',
        'V': 'Volume of the drink, ml',
        'z': 'Strength of the drink, d.u.',
        'M': 'Mass of the subject, kg',
        't': 'Time after alcohol intake, hours',
        'C': 'Ethanol concentration, g/kg or g/l',
        'r': 'Distribution coefficient d.u. or l/kg',
        'b': 'Elimination rate, g/kg/h or g/l/h',
        'a': 'Absorption constant, 1/h',
        'd': 'Extraction constant, 1/h',
        'k':'Equilibrium constant, 1/h',
        'R':'Concentration ratio, d.u.',
    }
    const requiredFields = formData.estimated_parameter === 'Biological fluid ethanol concentration'
        ? ['V', 'z', 'M', 't', 'r', 'b', 'a', 'd', 'k', 'R']
        : ['C', 'M', 't', 'r', 'b', 'a', 'd', 'k', 'R'];

    const [result, setResult] = useState(null);
    const [errors, setErrors] = useState([]);

    const validationRules = {
      V: { min: 0, max: 10000 },
      eV: { min: 0, max: 10000 },
      z: { min: 0, max: 1 },
      ez: { min: 0, max: 1 },
      M: { min: 1e-10, max: 200 },
      eM: { min: 0, max: 200 },
      t: { min: 1e-10, max: 1e10},
      et: { min: 0, max: 1e10},
      C: { min: 0, max: 1e10 },
      eC: { min: 0, max: 1e10},
      r: { min: 1e-10, max: 1,},
      er: { min: 0, max: 1 },
      b: { min: 1e-10, max: 0.7},
      eb: { min: 0, max: 0.7 },
      a: { min: 1, max: 8 },
      ea: { min: 0, max: 8},
      d: { min: 1e-10, max: 0.6 },
      ed: { min: 0, max: 0.6 },
      k: {min: 0, max: 1},
      ek: {min: 0, max: 1},
      R:{min:1e-10, max: 2},
      eR:{min: 0, max: 2}
    };

    const [isValidForm, setIsValidForm] = useState(true);
    const checkFormValidity = () => {
      const allRequiredFieldsFilled = requiredFields.every(field => {
        const value = formData[field];
        return value !== '' && value !== null && value !== undefined;
      });

      const hasNoErrors = Object.keys(errors).length === 0;

      return allRequiredFieldsFilled && hasNoErrors;
    };

    const handleInputChange = (e) => {
        let {name, value} = e.target;
        if(name !='estimated_parameter')
            value = parseFloat(value);

        const rules = validationRules[name];
        const newErrors = {};
        setFormData({...formData, [name]: value});
        if (isNaN(value) && name != 'estimated_parameter')
            newErrors[name] = `Value of ${names[name]}  must be float`;
        else if (rules) {
            if (typeof rules.min === 'number' && value < rules.min) {
                newErrors[name] = `Value of ${names[name]} must be more or equal ${rules.min}`;
            } else if (typeof rules.max === 'number' && value > rules.max) {
                newErrors[name] = `Value of ${names[name]}  must be less or equal ${rules.max}`;
            } else {
                delete newErrors[name];
            }
        }
        if (!newErrors[name]) {
            if(errors[name])
                delete errors[name];
            if(checkFormValidity())
                setIsValidForm(true);
        }
        else {
            setErrors(prevErrors => ({...prevErrors, ...newErrors}));
            setIsValidForm(false);
        }
    };
    const handleSubmit = async () => {
        try {
            const response = await axios.post('/api/alcohol-calcs/7', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'X-CSRFToken': getCookie('csrftoken')
                },
                withCredentials: true,
            });
            if (response.data.errors && response.data.errors.length > 0) {
                setErrors(response.data.errors);
                setResult(null);
                setPlotData(null);
            }
            else
                {
                    setErrors([]);
                    setPlotData(null);
                    setResult(response.data);
                    console.log(response.data);
                    if (response.data.points)
                        setPlotData(response.data.points);

                }
        } catch (error) {
            console.error('Error occurred:', error);
            setErrors([]);
            setPlotData(null);
            setResult(null);
            setErrors(['An error occurred while processing the request']);
        }
    };
    const createTraces = () => {
    return plotData.map((point, index) => ({
      x: point.x.map(Number),
      y: point.y.map(Number),
      type: 'scatter',
      mode: 'lines',
      name: point.config.title,
      line: { shape: 'spline' },
      xaxis: 'x',
      yaxis: 'y'
    }));
  };

  const layout = {
    title: 'Kinetics of ethanol concentration in blood and other body fluid',
    xaxis: {
      title: 't, hours',
      showgrid: true,
      zeroline: false
    },
    yaxis: {
      title: 'Ethanol concentration, g/kg or g/L',
      showgrid: true,
      zeroline: false
    },
    showlegend: true,
    legend: { orientation: 'h', y: -0.2 },
    margin: { t: 40, r: 40, b: 80, l: 60 },
    hovermode: 'closest'
  };


    return (
        <div className="content">
            <AlcoholCalcNavbar/>
            <div className="section section-components" style={{paddingTop: 40}}>
                <div className="container mb-5 pb-100">
                    <h1 className="h1 text-success font-weight-bold mb-4">Calculator of Body Ethanol Kinetics Following Single-Dose Oral Alcohol Intake </h1>
                    <h2 className="text-muted">Input parameter</h2>
                    <div className="form-group row">
                        <label className="col-sm-4 col-form-label">Estimated parameter:</label>
                        <div className="col-sm-8">
                            <label style={{display: "flex", alignItems: "center"}}>
                                <input
                                    type="radio"
                                    name="estimated_parameter"
                                    value="Biological fluid ethanol concentration"
                                    checked={formData.estimated_parameter === 'Biological fluid ethanol concentration'}
                                    onChange={handleInputChange}
                                />
                                <span style={{fontSize: 15, paddingLeft: 10}}>Biological fluid ethanol concentration</span>
                            </label>
                            <label style={{display: "flex", alignItems: "center"}}>
                                <input
                                    type="radio"
                                    name="estimated_parameter"
                                    value="Amount of alcohol and blood ethanol concentration"
                                    checked={formData.estimated_parameter === 'Amount of alcohol and blood ethanol concentration'}
                                    onChange={handleInputChange}
                                />
                                <span style={{fontSize: 15, paddingLeft: 10}}>Amount of alcohol and blood ethanol concentration</span>
                            </label>
                        </div>
                    </div>

                    {[["V", "eV"], ["z", "ez"], ["M", "eM"], ["t", "et"], ["C", "eC"], ["r", "er"], ["b", "eb"], ["a", "ea"], ["d", "ed"], ["k","ek"], ["R", "eR"]].map((fields) => {
                        const isRequired = requiredFields.includes(fields[0]);
                        return (
                            <>
                            <div className={"form-group row"}>
                                <label className={'col-sm-4 col-form-label'}
                                       htmlFor={fields[0]}>{(names[fields[0]])}</label>
                                <input
                                    className="col-sm-4 form-control"
                                    key={fields[0]}
                                    type="number"
                                    name={fields[0]}
                                    value={formData[fields[0]]}
                                    onChange={handleInputChange}
                                    disabled={!isRequired}
                                />
                                <label className={'col-sm-1 col-form-label'} htmlFor={fields[1]}>+-</label>
                                <input
                                    className="col-sm-3 form-control"
                                    key={fields[1]}
                                    type="number"
                                    name={fields[1]}
                                    value={formData[fields[1]]}
                                    onChange={handleInputChange}
                                    disabled={!isRequired}
                                />
                            </div>
                                <div className={"form-group row"}>
                                    {errors[fields[0]] && (
                                        <label className={'col-sm-4 col-form-label text-danger'}>{errors[fields[0]]}</label>
                                    )}
                                    {errors[fields[1]] && (
                                        <label className={'col-sm-4 col-form-label text-danger'}>{errors[fields[1]]}</label>
                                    )}

                                </div>
                            </>
                        )
                    })}

                    <button onClick={handleSubmit} disabled={!isValidForm} className="btn btn-secondary mb-2">
                        Calculate
                    </button>
                    <div className="mt-2 mb-3">
                        <small className="text-muted">
                            By clicking "Calculate" you agree to the{' '}
                            <Link to="/agreement">User Agreement</Link>
                            .
                        </small>
                    </div>
                    {result && (
                        <div className="mt-5">
                            <h2 className="text-muted mb-3">Calculation Results</h2>
                            {result['type'] == 'concentration' && (
                                <div>
                                    <h2>Biological fluid ethanol concentration</h2>
                                    <h3>Kinetic parameters</h3>
                                    <p><b>C(t)</b> = {result['Ct']} +- {result['dC']} g/kg or g/L</p>
                                    <p>Lower limit {result['lC']},  g/kg or g/L</p>
                                    <p>Upper limit {result['uC']},  g/kg or g/L</p>
                                    <h3>Other kinetic parameters of biological fluid</h3>
                                    <p>Maximum ethanol concentration {result['Cmax']}</p>
                                    <p>Time to reach the maximum concentration of ethanol {result['tmax']}</p>
                                    <p>Time of complete ethanol removal {result['tf']}</p>
                                    <p>False Elimination Period {result['te']} - {result['tmax']}</p>
                                </div>
                            )}
                            {result['type'] == 'alcohol_amount' && (
                                <div>
                                    <h2>Amount of alcohol consumed</h2>
                                    <p>m = {result['m']} +- {result['dm']}, g</p>
                                    <p>Lower limit {result['lm']}, g </p>
                                    <p>Upper limit {result['um']}, g</p>
                                    <h3>Blood ethanol concentration</h3>
                                    <p>C(t) {result['Ct']} +- {result['dC']}, g/kg or g/L</p>
                                    <p>Lower limit {result['lC']}, g/kg or g/L</p>
                                    <p>Upper limit {result['uC']}, g/kg or g/L</p>
                                </div>
                            )}
                            {plotData && (<div style={{width: '100%', height: '80vh'}}>
                                <Plot
                                        data={createTraces()}
                                        layout={layout}
                                        style={{ width: '100%', height: '100%' }}
                                        config={{ responsive: true }}
                                      />
                            </div>)}
                        </div>
                    )}

                    {errors.length > 0 && (
                        <div className="alert alert-danger mt-4">
                            {errors.map((error, idx) => (
                                <div key={idx}>{error}</div>
                            ))}
                        </div>
                    )}

                </div>
            </div>
        </div>)
};

export default AlcoholCalcV7;